import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  SalesWinLossDigest,
  WinLossPattern,
  mergeBrainWithCrmMetrics,
  attachClosingFactorWeights,
  CrmWinLossMetrics,
} from '../../utils/salesWinLossAnalytics';
import { parseBrainSummary } from '../../utils/formatBrainSummary';
import ClosingFactorCard from './ClosingFactorCard';

interface Props {
  digest: SalesWinLossDigest;
  crmMetrics: CrmWinLossMetrics;
}

const WinLossBrainPanel: React.FC<Props> = ({ digest, crmMetrics }) => {
  const parsed = useMemo(
    () => (digest.summary ? parseBrainSummary(digest.summary) : { sections: [] }),
    [digest.summary],
  );

  const patternItems = useMemo(
    () => mergeBrainWithCrmMetrics(digest, crmMetrics),
    [digest, crmMetrics],
  );

  const { items: weightedItems, leftoverFactors } = useMemo(
    () => attachClosingFactorWeights(patternItems, digest.closingFactors || []),
    [patternItems, digest.closingFactors],
  );

  const winPatterns = weightedItems.filter(p => p.type === 'win');
  const lossPatterns = weightedItems.filter(p => p.type === 'loss');
  const allFactors = digest.closingFactors || [];
  const chipsToShow =
    leftoverFactors.length === 0
      ? []
      : patternItems.length === 0
        ? leftoverFactors
        : leftoverFactors.length === allFactors.length
          ? []
          : leftoverFactors;

  const journeyTotal =
    parsed.journeyStats?.total ?? crmMetrics.wonCount + crmMetrics.lostCount;
  const journeyWins = parsed.journeyStats?.wins ?? crmMetrics.wonCount;
  const journeyLosses = parsed.journeyStats?.losses ?? crmMetrics.lostCount;

  const hasPatterns = winPatterns.length > 0 || lossPatterns.length > 0;
  const hasFactors = chipsToShow.length > 0;
  const hasAnalysis = parsed.sections.length > 0;
  const [analysisOpen, setAnalysisOpen] = useState(() => !hasPatterns && hasAnalysis);

  if (!hasPatterns && !hasFactors && !digest.summary) return null;

  return (
    <section className="fin-forecast-table-card sa-brain-panel" aria-label="Win and loss patterns">
      <header className="sa-brain-panel-header">
        <div className="sa-brain-panel-title-row">
          <h2 className="sa-brain-panel-title">Win / loss patterns</h2>
          <p className="sa-brain-panel-meta">
            <span>{digest.report_date}</span>
            <span>
              {journeyTotal} journeys · {journeyWins} won · {journeyLosses} lost
              {crmMetrics.closeRate > 0 ? ` · ${crmMetrics.closeRate.toFixed(0)}% close` : ''}
            </span>
          </p>
        </div>
      </header>

      {hasPatterns && (
        <div className="sa-brain-columns">
          <PatternColumn title="Win drivers" tone="win" items={winPatterns} empty="No win patterns in this digest." />
          <PatternColumn title="Loss drivers" tone="loss" items={lossPatterns} empty="No loss patterns in this digest." />
        </div>
      )}

      {hasFactors && (
        <div className="sa-brain-closing-factors">
          <h3 className="sa-brain-subheading">Other closing factors</h3>
          <ul className="sa-brain-factor-chips">
            {chipsToShow.map((f, i) => (
              <li
                key={`${f.factor}-${i}`}
                className={`sa-brain-factor-chip sa-brain-factor-chip--${f.direction}`}
              >
                {f.factor}
                {f.weight !== undefined && (
                  <span className="sa-brain-factor-weight">{Math.round(f.weight * 100)}%</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasAnalysis && (
        <div className="sa-brain-analysis">
          {hasPatterns ? (
            <>
              <button
                type="button"
                className="sa-brain-analysis-toggle"
                onClick={() => setAnalysisOpen(o => !o)}
                aria-expanded={analysisOpen}
              >
                {analysisOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                Full analysis notes
                <span className="sa-brain-analysis-meta">{parsed.sections.length} sections</span>
              </button>
              {analysisOpen && (
                <div className="sa-brain-analysis-body">
                  {parsed.sections.map(section => (
                    <AnalysisSection key={section.id} section={section} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="sa-brain-analysis-body">
              {parsed.sections.map(section => (
                <AnalysisSection key={section.id} section={section} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

function PatternColumn({
  title,
  tone,
  items,
  empty,
}: {
  title: string;
  tone: 'win' | 'loss';
  items: {
    pattern: WinLossPattern;
    type: 'win' | 'loss';
    crmWinRate?: number;
    weight?: number;
  }[];
  empty: string;
}) {
  return (
    <div className={`sa-brain-col sa-brain-col--${tone}`}>
      <h3 className="sa-brain-subheading">{title}</h3>
      {items.length === 0 ? (
        <p className="sa-muted">{empty}</p>
      ) : (
        <div className="sa-pattern-list">
          {items.map(({ pattern, type, crmWinRate, weight }) => (
            <ClosingFactorCard
              key={`${type}-${pattern.id}`}
              pattern={pattern}
              type={type}
              crmWinRate={crmWinRate}
              weight={weight}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnalysisSection({ section }: { section: ReturnType<typeof parseBrainSummary>['sections'][0] }) {
  return (
    <article className={`sa-brain-section sa-brain-section--${section.tone}`}>
      <h4 className="sa-brain-section-title">{section.title}</h4>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="sa-brain-section-p">
          {p}
        </p>
      ))}
      {section.bullets.length > 0 && (
        <ul className="sa-brain-section-list">
          {section.bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}
    </article>
  );
}

export default WinLossBrainPanel;
